import { FC } from 'react';
import { Stack, Typography } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { FormattedMessage } from 'react-intl';
import { MetricValueComparison } from './widgets/metrics/MetricValueComparison';
import { graphql } from '../../../gql';
import { useApiQuery } from '../../../query/hooks';
import { metricFormatValue } from '../../utils/metricFormatValue';
import { getRangesWithDefault } from './widgets/metrics/metricRange';

const displayMetricQuery = graphql(`
	query journey_displayMainMetric(
		$id: UUID!
		$compareStart: DateTime!
		$compareEnd: DateTime!
		$targetStart: DateTime!
		$targetEnd: DateTime!
	) {
		displayMetric(id: $id) {
			name
			expectations
			precision
			trendComparison
			values(compareRange: { start: $compareStart, end: $compareEnd }, targetRange: { start: $targetStart, end: $targetEnd }) {
				targetValue
				comparison
				lastUpdateAt
			}
		}
	}
`);

type Props = Readonly<{
	metricId: string;
	unitSign: string | null;
}>;

const MainMetricInfo: FC<Props> = ({ metricId, unitSign }) => {
	const {
		compareRange: { start: compareStart, end: compareEnd },
		targetRange: { start: targetStart, end: targetEnd },
	} = getRangesWithDefault(undefined);

	const { data, isLoading } = useApiQuery(displayMetricQuery, {
		variables: {
			id: metricId,
			compareStart,
			compareEnd,
			targetStart,
			targetEnd,
		},
	});

	if (isLoading) {
		return (
			<Stack minWidth={100} height={'1.7em'} justifyContent={'center'}>
				<LinearProgress />
			</Stack>
		);
	}

	return (
		<Stack direction={'row'} alignItems={'center'} gap={1} flexWrap={'wrap'}>
			{data?.displayMetric != null && (
				<>
					<Typography noWrap fontSize={14}>
						<FormattedMessage id={'main-metric-info.name'} values={{ name: data.displayMetric.name }} />
					</Typography>
					<Stack direction={'row'} gap={1} alignItems={'center'} flexWrap={'wrap'}>
						{data.displayMetric.values.targetValue != null && data.displayMetric.precision != null ? (
							<Typography fontSize={14}>
								{metricFormatValue(data.displayMetric.values.targetValue, data.displayMetric.precision)}&nbsp;{unitSign}
							</Typography>
						) : (
							<FormattedMessage id={'common.not-available'} />
						)}
						<Stack direction={'row'} alignItems={'center'} marginLeft={-0.75}>
							<MetricValueComparison metric={data.displayMetric} />
						</Stack>
					</Stack>
				</>
			)}
		</Stack>
	);
};

export { MainMetricInfo };
